var _excluded = ["children", "leftIcon", "rightIcon", "label"];

function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

function _objectWithoutPropertiesLoose(source, excluded) { if (source == null) return {}; var target = {}; var sourceKeys = Object.keys(source); var key, i; for (i = 0; i < sourceKeys.length; i++) { key = sourceKeys[i]; if (excluded.indexOf(key) >= 0) continue; target[key] = source[key]; } return target; }

import React from 'react';
import { styled, forwardRef } from '@grupoboticario/flora-react-system';
import { AccessibleIcon } from '@radix-ui/react-accessible-icon';
var DEFAULT_TAG = 'span';
var cssTransparent = 'transparent';
var tokenNonInteractiveAltPredominant = '$nonInteractiveAltPredominant';
var tokenNonInteractivePredominant = '$nonInteractivePredominant';
var tokenDisabledContent = '$disabledContent';
var tokenDisabledAltBackground = '$disabledAltBackground';
var tokenErrorStandard = '$error-standard';
var tokenErrorAlt = '$error-alt';
var tokenSuccessStandard = '$success-standard';
var tokenSuccessAlt = '$success-alt';
var tokenAlertStandard = '$alert-standard';
var tokenAlertAlt = '$alert-alt';
var StyledTag = styled(DEFAULT_TAG, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  appearance: 'none',
  borderWidth: '0',
  boxSizing: 'border-box',
  flexShrink: 0,
  verticalAlign: 'middle',
  outline: 'none',
  textDecoration: 'none',
  WebkitTapHighlightColor: 'rgba(0,0,0,0)',
  gap: '$1',
  borderRadius: '$small',
  color: tokenNonInteractivePredominant,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  fontSize: '$restricted',
  lineHeight: '16px',
  minWidth: '$3',
  minHeight: 24,
  border: "1px solid " + cssTransparent,
  textTransform: 'unset',
  '&:focus': {
    boxShadow: '$outline'
  },
  '& svg': {
    size: 12,
    cursor: 'pointer',
    '&:hover': {
      background: '$disabled',
      borderRadius: '$pill'
    }
  },
  variants: {
    variant: {
      disabled: {},
      assorted9: {},
      error: {},
      success: {},
      alert: {},
      primary: {
        backgroundColor: '$actionableDefault',
        color: '$backgroundPrimary',
        fontWeight: 'bold'
      }
    },
    shape: {
      solid: {},
      ghost: {}
    },
    alt: {
      true: {},
      false: {}
    },
    size: {
      small: {
        maxHeight: 16,
        minHeight: 16,
        padding: '0 $1'
      },
      medium: {
        padding: '$1 $2'
      }
    }
  },
  compoundVariants: [// disabled
  {
    shape: 'solid',
    alt: 'false',
    variant: 'disabled',
    css: {
      backgroundColor: '$disabledBackground',
      color: tokenDisabledContent
    }
  }, {
    shape: 'ghost',
    alt: 'false',
    variant: 'disabled',
    css: {
      backgroundColor: cssTransparent,
      color: tokenDisabledContent,
      border: "1px solid " + tokenDisabledContent
    }
  }, // disabled alt
  {
    shape: 'solid',
    alt: 'true',
    variant: 'disabled',
    css: {
      backgroundColor: tokenDisabledAltBackground,
      color: '$disabledAltContent'
    }
  }, {
    shape: 'ghost',
    alt: 'true',
    variant: 'disabled',
    css: {
      backgroundColor: cssTransparent,
      color: '$disabledAltContent',
      border: "1px solid " + tokenDisabledAltBackground
    }
  }, // assorted9
  {
    shape: 'solid',
    alt: 'false',
    variant: 'assorted9',
    css: {
      backgroundColor: '$9',
      color: tokenNonInteractiveAltPredominant
    }
  }, {
    shape: 'ghost',
    alt: 'false',
    variant: 'assorted9',
    css: {
      backgroundColor: cssTransparent,
      color: '$9',
      border: '1px solid $9'
    }
  }, // assorted9 alt
  {
    shape: 'solid',
    alt: 'true',
    variant: 'assorted9',
    css: {
      backgroundColor: '$9',
      color: tokenNonInteractiveAltPredominant
    }
  }, {
    shape: 'ghost',
    alt: 'true',
    variant: 'assorted9',
    css: {
      backgroundColor: cssTransparent,
      color: '$9',
      border: '1px solid $9'
    }
  }, // error
  {
    shape: 'solid',
    alt: 'false',
    variant: 'error',
    css: {
      backgroundColor: tokenErrorStandard,
      color: tokenNonInteractiveAltPredominant
    }
  }, {
    shape: 'ghost',
    alt: 'false',
    variant: 'error',
    css: {
      backgroundColor: cssTransparent,
      color: tokenErrorStandard,
      border: "1px solid " + tokenErrorStandard
    }
  }, // error alt
  {
    shape: 'solid',
    alt: 'true',
    variant: 'error',
    css: {
      backgroundColor: tokenErrorAlt,
      color: tokenNonInteractivePredominant
    }
  }, {
    shape: 'ghost',
    alt: 'true',
    variant: 'error',
    css: {
      backgroundColor: cssTransparent,
      color: tokenErrorAlt,
      border: "1px solid " + tokenErrorAlt
    }
  }, // success
  {
    shape: 'solid',
    alt: 'false',
    variant: 'success',
    css: {
      backgroundColor: tokenSuccessStandard,
      color: tokenNonInteractiveAltPredominant
    }
  }, {
    shape: 'ghost',
    alt: 'false',
    variant: 'success',
    css: {
      backgroundColor: cssTransparent,
      color: tokenSuccessStandard,
      border: "1px solid " + tokenSuccessStandard
    }
  }, // success alt
  {
    shape: 'solid',
    alt: 'true',
    variant: 'success',
    css: {
      backgroundColor: tokenSuccessAlt,
      color: tokenNonInteractivePredominant
    }
  }, {
    shape: 'ghost',
    alt: 'true',
    variant: 'success',
    css: {
      backgroundColor: cssTransparent,
      color: tokenSuccessAlt,
      border: "1px solid " + tokenSuccessAlt
    }
  }, // alert
  {
    shape: 'solid',
    alt: 'false',
    variant: 'alert',
    css: {
      backgroundColor: tokenAlertStandard,
      color: tokenNonInteractiveAltPredominant
    }
  }, {
    shape: 'ghost',
    alt: 'false',
    variant: 'alert',
    css: {
      backgroundColor: cssTransparent,
      color: tokenAlertStandard,
      border: "1px solid " + tokenAlertStandard
    }
  }, // alert
  {
    shape: 'solid',
    alt: 'true',
    variant: 'alert',
    css: {
      backgroundColor: tokenAlertAlt,
      color: tokenNonInteractivePredominant
    }
  }, {
    shape: 'ghost',
    alt: 'true',
    variant: 'alert',
    css: {
      backgroundColor: cssTransparent,
      color: tokenAlertAlt,
      border: "1px solid " + tokenAlertAlt
    }
  }],
  defaultVariants: {
    variant: 'disabled',
    shape: 'solid',
    alt: 'false',
    size: 'medium'
  }
});

/**
 * @component
 * @name Tag
 * @description Tag component
 * @tutorial https://flora.gb.tech/docs/components/core/tag
 */
export var Tag = forwardRef((_ref, forwardedRef) => {
  var {
    children,
    leftIcon,
    rightIcon,
    label = 'icon'
  } = _ref,
      props = _objectWithoutPropertiesLoose(_ref, _excluded);

  return /*#__PURE__*/React.createElement(StyledTag, _extends({}, props, {
    "data-flora": "tag",
    ref: forwardedRef
  }), leftIcon && /*#__PURE__*/React.createElement(TagIcon, {
    as: leftIcon,
    label: label
  }), children, rightIcon && /*#__PURE__*/React.createElement(TagIcon, {
    as: rightIcon,
    label: label
  }));
});

var TagIcon = _ref2 => {
  var {
    label = 'icon',
    as
  } = _ref2;
  return /*#__PURE__*/React.createElement(AccessibleIcon, {
    label: label,
    "data-flora": "tag"
  }, /*#__PURE__*/React.cloneElement(as));
};
//# sourceMappingURL=Tag.js.map